/**
 * Trạng thái của phiên kết toán
 * Map với SettlementResponseStatusEnum từ API
 */
export enum FinalizationStatus {
  PENDING = "PENDING", // Chờ xử lý (vừa tạo, chưa chuyển khoản)
  SUCCESS = "SUCCESS", // Hoàn thành (đã chuyển khoản)
  CANCELLED = "CANCELLED", // Đã hủy
  FAIL = "FAIL", // Thất bại
  CUT_OVER = "CUT_OVER", // Cắt chuyển
}

/**
 * Model chính cho 1 phiên kết toán
 * Map với SettlementResponse từ API
 */
export interface FinalizationItemModel {
  // ===== Identifiers =====
  id: string; // settlementId từ API
  code: string; // settlementCode từ API

  // ===== Thời gian kết toán =====
  fromDate: string; // startDate từ API
  toDate: string; // endDate từ API

  // ===== Thông tin công ty =====
  companyId: string; // merchantId từ API
  companyName: string; // merchantName từ API

  // ===== Dữ liệu tài chính =====
  totalRevenue: number; // totalRevenue từ API (VND)
  tax: number; // totalVatChargeAmount từ API (VND)
  serviceFeeHold: number; // totalChargeAmount từ API (VND)
  paymentTransferAmount: number; // totalActualAmount từ API (VND)

  // ===== Metadata =====
  totalTransaction: number; // totalTransaction từ API
  status: FinalizationStatus; // status từ API

  // ===== Audit fields =====
  createBy?: string; // settlementBy từ API (có thể không có)
  createDate?: string; // settlementDateTime từ API (ISO 8601)
  updateBy?: string; // Người cập nhật cuối cùng
  updateDate?: string; // Ngày cập nhật cuối (ISO 8601)

  // ===== Chứng từ =====
  documents?: DocumentModel[]; // Danh sách file chứng từ đính kèm (có thể không có trong API)
}

/**
 * Model cho chứng từ đính kèm
 */
export interface DocumentModel {
  id: string;
  fileName: string; // Tên file (example.pdf)
  fileUrl: string; // URL download
  uploadDate: string; // Ngày upload
  uploadBy: string; // Người upload
  fileSize: number; // Kích thước (bytes)
  fileType: string; // Loại file (pdf, xlsx, png...)
}

/**
 * Model cho Pageable response (dùng chung)
 */
export interface PageableModel<T> {
  items: T[]; // Danh sách items
  pageNumber: number; // Page hiện tại (0-based)
  pageSize: number; // Số items trên 1 page
  totalSize: number; // Tổng số items
  totalPage: number; // Tổng số pages
}

/**
 * Request params cho getListFinalization
 * Map với getSettlementByMerchant/getSettlements từ API
 */
export interface GetListFinalizationParams {
  page?: number; // Page number (0-based, default: 0)
  size?: number; // Page size (default: 10)
  sort?: string[]; // Sort criteria ["updateDate,desc"]
  keyword?: string; // textSearch từ API (search in company name, code)
  fromDate?: string; // Filter: kết toán từ ngày
  toDate?: string; // Filter: kết toán đến ngày
  companyId?: string; // Filter: theo company ID (merchantId)
  statuses?: FinalizationStatus[]; // Filter: theo status (chỉ 1 status từ API)
}

/**
 * Model cho giao dịch trong settlement detail
 * Map với TransactionResponse từ API
 */
export interface TransactionModel {
  id: string; // id từ API
  refTransactionId?: string; // refTransactionId từ API
  txnNumber?: string; // txnNumber từ API
  status?: string; // status từ API
  statusVi?: string; // statusVi từ API
  statusViPortal?: string; // statusViPortal từ API
  amount: number; // amount từ API (VND)
  chargeAmount?: number; // chargeAmount từ API
  vatChargeAmount?: number; // vatChargeAmount từ API
  actualAmount?: number; // actualAmount từ API
  createDateTime?: string; // createDateTime từ API
  completeTime?: string; // completeTime từ API
  virtualAccount?: string; // virtualAccount từ API
  description?: string; // description từ API
  paymentType?: string; // paymentType từ API
  customerName?: string; // customerName từ API
  customerPhone?: string; // customerPhone từ API
  customerEmail?: string; // customerEmail từ API
  customerAddress?: string; // customerAddress từ API
  payLinkCode?: string; // payLinkCode từ API
  payPageCode?: string; // payPageCode từ API
  accountNo?: string; // accountNo từ API
  accountName?: string; // accountName từ API
  interBankTrace?: string; // interBankTrace từ API
  forControl?: boolean; // forControl từ API
}

/**
 * Model cho settlement detail (có thêm transactions)
 * Extend từ FinalizationItemModel
 */
export interface SettlementDetailModel extends FinalizationItemModel {
  transactions?: PageableModel<TransactionModel>; // Danh sách giao dịch với pagination
}

export interface ExportMerchantSettlementParams {
  textSearch?: string;
  fromDate?: string;
  toDate?: string;
  status?: FinalizationStatus;
}
